import React from 'react';
import {
	Box,
	Chip,
	Tooltip,
	Typography,
} from '@barracuda-internal/bds-core-v2';
import { DateTime } from 'luxon';
import { caseCloudTypeInfo } from './helper';
import { CaseCloudTypeInfo } from 'src/components/cases/CloudTypeCell';

interface EllipsisTooltipProps {
	title: string;
	children: React.ReactNode;
}

export const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({
	title,
	children,
}) => (
	<Tooltip
		title={
			<Box
				style={{
					maxHeight: '250px',
					overflowY: 'auto',
				}}
			>
				{typeof title === 'string'
					? title.split('\n').map((line, idx) => (
							<Typography key={idx} variant="body2">
								{line}
							</Typography>
					  ))
					: title}
			</Box>
		}
		arrow
		componentsProps={{
			tooltip: {
				style: {
					maxWidth: '400px',
					minWidth: '200px',
				},
			},
		}}
	>
		<Box
			style={{
				maxWidth: '250px',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
			}}
		>
			{children}
		</Box>
	</Tooltip>
);

export function MoreRenderer({ tags }: { tags: string[] }): JSX.Element | null {
	if (!tags || tags.length === 0) return null;
	return (
		<Tooltip
			title={
				<div>
					{tags.map((tag, idx) => (
						<Typography
							key={idx}
							variant="body2"
							style={{
								display: 'block',
								padding: '4px 8px',
								whiteSpace: 'pre-line',
							}}
						>
							{tag}
						</Typography>
					))}
				</div>
			}
			arrow
		>
			<Typography
				variant="body2"
				color="textSecondary"
				style={{ cursor: 'pointer' }}
			>
				+{tags.length} more
			</Typography>
		</Tooltip>
	);
}

export const getTagStyle = (tag: string): Record<string, string> => {
	let color: string | null = null;
	let textColor = 'black';
	switch (tag) {
		case 'Malicious':
		case 'Suspicious':
			color = '#DE3131';
			textColor = 'white';
			break;
		case 'Credit Card':
		case 'Credentials':
		case 'Passport':
		case 'License Number':
		case 'Personal ID':
		case 'Tax ID':
		case 'MICR Code':
		case 'Personal Medical ID':
		case 'Financial ID':
			color = '#66DAEE';
			break;
		case 'External Readable':
		case 'External Writeable':
		case 'Public Readable':
		case 'Public Writeable':
			color = '#F2EB7B';
			break;
		default:
			color = '#C8C8C7';
			break;
	}
	return {
		margin: '2px',
		background: color,
		border: `1px solid ${color}`,
		color: textColor,
	};
};

interface StatusRendererProps {
	dataItem: { statusReason: string };
	tab: string;
}

export function StatusRenderer({
	dataItem,
	tab,
}: StatusRendererProps): JSX.Element {
	let action = '';
	switch (dataItem.statusReason) {
		case 'UserDeleted':
		case 'PolicyDeleted':
			action = tab == 'Resolved' ? 'Deleted' : 'Deleting';
			break;
		case 'UserQuarantined':
		case 'PolicyQuarantined':
			action = tab == 'Resolved' ? 'Quarantined' : 'Quarantining';
			break;
		case 'UserUnShared':
		case 'PolicyUnShared':
			action = tab == 'Resolved' ? 'Unshared' : 'Unsharing';
			break;
		case 'UserAction':
			action = tab == 'Resolved' ? 'Dismissed' : 'Dismissing';
			break;
		default:
			action = tab == 'Resolved' ? 'Dismissed' : 'Dismissing';
			break;
	}
	return (
		<Chip
			clickable={false}
			label={
				<>
					<Typography component="span">{action}</Typography>
				</>
			}
			size="small"
			sx={{
				background: '#EDE3F6',
				border: '0px',
				color: 'black',
				margin: '2px',
			}}
		/>
	);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderCell = (
	accessorKey: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	cell: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	row?: any,
	tab?: number,
): React.ReactNode => {
	switch (accessorKey) {
		case 'lastModified': {
			const value = cell.getValue();
			if (!value) return 'N/A';

			const localZone = DateTime.local().zoneName;

			const parsedDate = DateTime.fromISO(value, { zone: 'utc' }).setZone(
				localZone,
			);
			return parsedDate.isValid
				? parsedDate.toFormat('MM/dd/yyyy, h:mm a')
				: 'Invalid Date';
		}
		case 'statusReason': {
			const value = cell.getValue();
			const tabLabel =
				tab === 0
					? 'Unresolved'
					: tab === 1
					? 'In Progress'
					: 'Resolved';

			return value ? (
				<StatusRenderer dataItem={row.original} tab={tabLabel} />
			) : (
				'-'
			);
		}

		case 'cloudType': {
			const value = cell.getValue();
			return value?.length > 0 ? `${caseCloudTypeInfo(value)}` : '-';
		}

		case 'fileName': {
			const fileName = cell.getValue();
			const dataItem = row?.original;
			const filePath = dataItem?.filePath;
			const cloudType = dataItem?.cloudType;
			const filePathText = Array.isArray(filePath)
				? filePath.join(' > ')
				: filePath;
			return fileName?.length > 0 ? (
				<Box>
					{fileName.length > 64 ? (
						<EllipsisTooltip title={fileName}>
							<Typography
								variant="body2"
								color="textPrimary"
								noWrap
							>
								{fileName}
							</Typography>
						</EllipsisTooltip>
					) : (
						<Typography variant="body2" color="textPrimary" noWrap>
							{fileName}
						</Typography>
					)}
					{filePath && (
						<EllipsisTooltip
							title={`${caseCloudTypeInfo(
								cloudType,
							)} > ${filePathText}`}
						>
							<Typography
								variant="caption"
								color="textSecondary"
								noWrap
							>
								{cloudType && (
									<CaseCloudTypeInfo cloudType={cloudType} />
								)}
								{' > '}
								{filePathText}
							</Typography>
						</EllipsisTooltip>
					)}
				</Box>
			) : (
				'-'
			);
		}

		case 'ownerNames': {
			const value = cell.getValue();
			if (!value || value.length === 0) return '-';
			const [first, ...rest] = value;
			return (
				<Box>
					<Typography variant="body2">{first}</Typography>
					<MoreRenderer tags={rest} />
				</Box>
			);
		}

		case 'resolvedBy': {
			const value = cell.getValue();
			if (!value || value.length === 0) return '-';
			const [first, ...rest] = value;
			return (
				<Box>
					<Typography variant="body2">{first}</Typography>
					<MoreRenderer tags={rest} />
				</Box>
			);
		}

		case 'significantFindingCategories': {
			const value = cell.getValue();
			return value && value?.length > 0
				? value.map((item: string, index: number) => (
						<div key={index}>{item}</div>
				  ))
				: '-';
		}

		case 'sharingLevels': {
			const value = cell.getValue();
			return value && value?.length > 0 ? value.join(', ') : '-';
		}

		case 'tags': {
			const tags = cell.getValue() ?? {};
			const entries = Object.entries(tags);
			if (entries.length === 0) return '-';

			const visibleTags = entries.slice(0, 3);
			const moreTags = entries
				.slice(3)
				.map(([key, val]) => `${key} ${val}`);

			return (
				<Box
					style={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: '2px',
					}}
				>
					{visibleTags.map(([tagName, count]) => {
						const label = `${tagName} ${count}`;
						const chipLabel =
							tagName.length > 19 ? (
								<Tooltip title={label} arrow>
									<span>
										{tagName.slice(0, 16)}... {count}
									</span>
								</Tooltip>
							) : (
								label
							);
						return (
							<Chip
								key={tagName}
								label={chipLabel}
								size="small"
								style={getTagStyle(tagName)}
							/>
						);
					})}
					<MoreRenderer tags={moreTags} />
				</Box>
			);
		}

		default:
			return cell.getValue() ?? '-';
	}
};
